import Swal from "sweetalert2";
import {
  Col,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
  FormGroup,
  InputGroupText,
  InputGroup,
} from "reactstrap";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { P, H5 } from "../../../../../AbstractElements";
import { useState, useEffect } from "react";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";
import { Save } from "react-feather";

//TYPE AND SERVICES

import {
  applicantsService,
  APPLICANTS_QUERY_KEY,
  VIEW_CANDIDATES_QUERY_KEY,
} from "../../../../../Services/applicantsService";

// COMPANY MASTER
import {
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
  companyprofileService,
} from "../../../../../Services/companyProfileService";

// ADDRESS
import {
  countryService,
  COUNTRY_QUERY_KEY,
} from "../../../../../Services/countryService";
import { Country } from "../../../../../Types/MasterData/Country";
import {
  provinceService,
  PROVINCE_QUERY_KEY,
} from "../../../../../Services/provinceService";
import { Province } from "../../../../../Types/MasterData/Province";
import {
  cityService,
  CITY_QUERY_KEY,
} from "../../../../../Services/cityService";
import { City } from "../../../../../Types/MasterData/City";
import {
  barangayService,
  BARANGAY_QUERY_KEY,
} from "../../../../../Services/barangayService";
import { Barangay } from "../../../../../Types/MasterData/Barangay";

//INTERFACE
interface UpdateFormProps {
  toggle?: () => void;
  ApplNo: string;
}

const UpdateAddressModal: React.FC<UpdateFormProps> = ({ toggle, ApplNo }) => {
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    address_line1: "",
    address_line2: "",
    country: "",
    province: "",
    city: "",
    barangay: "",
    zip_code: "",
  });

  //FETCH COMPANY DETAILS
  const { data: applicationData, isLoading: isFetching } = useQuery({
    queryKey: [APPLICANTS_QUERY_KEY, ApplNo],
    queryFn: () => applicantsService.getApplicantsByApplicationNo(ApplNo),
  });

  useEffect(() => {
    if (applicationData) {
      setFormData({
        address_line1: applicationData.data.address_line1 || "",
        address_line2: applicationData.data.address_line2 || "",
        country: "",
        province: "",
        city: "",
        barangay: "",
        zip_code: "",
      });
    }
  }, [applicationData]);

  const [countryCode, setCountryCode] = useState<string | null>(null);
  const [provinceId, setProvinceId] = useState<string | null>(null);
  const [cityId, setCityId] = useState<string | null>(null);

  //LIST OF VALUE
  const { data: countryData, isLoading: isCountryLoading } = useQuery<{
    data: Country[];
  }>({
    queryKey: COUNTRY_QUERY_KEY,
    queryFn: countryService.getAllCountry,
  });
  const countries: Country[] =
    countryData?.data?.filter((item) => item.status === 1) ?? [];

  const {
    data: provinceData,
    isLoading: isProvinceLoading,
    isError: isProvinceError,
  } = useQuery<{ data: Province[] }>({
    queryKey: [PROVINCE_QUERY_KEY, countryCode],
    queryFn: () => provinceService.getProvinceByCountry(countryCode as string),
    enabled: !!countryCode,
  });

  const provinces: Province[] =
    provinceData?.data?.filter((item) => item.status === 1) ?? [];

  const provinceOptions = !isProvinceError
    ? provinces.map((prov) => ({
        id: prov.id,
        label: prov.name,
      }))
    : [];

  const {
    data: cityData,
    isLoading: isCityLoading,
    isError: isCityError,
  } = useQuery<{ data: City[] }>({
    queryKey: [CITY_QUERY_KEY, provinceId],
    queryFn: () => cityService.getCityByProvince(provinceId as string),
    enabled: !!provinceId,
  });

  const cities: City[] =
    cityData?.data?.filter((item) => item.status === 1) ?? [];

  const cityOptions = !isCityError
    ? cities.map((city) => ({
        id: city.id,
        label: city.name,
      }))
    : [];

  const {
    data: barangayData,
    isLoading: isBarangayLoading,
    isError: isBarangayError,
  } = useQuery<{ data: Barangay[] }>({
    queryKey: [BARANGAY_QUERY_KEY, cityId],
    queryFn: () => barangayService.getBarangayByCity(cityId as string),
    enabled: !!cityId,
  });

  const barangay: Barangay[] =
    barangayData?.data?.filter((item) => item.status === 1) ?? [];

  const barangayOptions = !isBarangayError
    ? barangay.map((barangay) => ({
        id: barangay.id,
        label: barangay.name,
      }))
    : [];

  const handleProvinceSelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      province: "",
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setCountryCode(e.target.value);
    setProvinceId("");
    setCityId("");
  };

  const handleCitySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      city: "",
      barangay: "",
      zip_code: "",
    }));
    setProvinceId(e.target.value);
    setCityId("");
  };

  const handleBarangaySelect = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      barangay: "",
      zip_code: "",
    }));
    setCityId(e.target.value);
  };

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      applicantsService.updateAddress(ApplNo, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries();
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "success",
        title: "Transaction Successful",
        text: result.message,
        confirmButtonColor: "#4e73df",
      });
      if (toggle) toggle();
    },
    onError: (error: any) => {
      const responseData = error.response?.data;
      Swal.close();
      Swal.fire({
        allowOutsideClick: false,
        allowEscapeKey: false,
        icon: "error",
        title: "Error",
        text: responseData?.message || "Failed to update application form.",
        confirmButtonColor: "#4e73df",
      });
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update this record?",
      icon: "question",
      showCancelButton: true,

      confirmButtonText: '<i class="fa fa-save"></i> Update Information',
      cancelButtonText: '<i class="fa fa-times"></i> Cancel',

      confirmButtonColor: "#4e73df", // Primary
      cancelButtonColor: "#dc3545", // Danger
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Uploading...",
          text: "Please wait while your profile photo is being uploaded.",
          allowOutsideClick: false,
          allowEscapeKey: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        updateMutation.mutate(formData);
      }
    });
  };

  return (
    <ModalBody className="large-modal-body modal-body">
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <div className="d-flex justify-content-between align-items-center">
          <H5> </H5>
          <P className="mb-2">
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>{" "}
            Fields marked with <b className="text-danger">*</b> are required{" "}
            <i className="icofont icofont-exclamation-tringle txt-warning"></i>
          </P>
        </div>

        <Row className="g-3">
          <div className="p-3 card-wrapper border rounded-3 h-100">
            <div className="d-flex justify-content-between align-items-center">
              <H5 className="mt-1 mb-2">Home Address</H5>
            </div>
            <Row className="p-1">
              <Col md="12">
                <FormGroup>
                  <Label>
                    Address Line 1 <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-home txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      type="text"
                      placeholder="Street / House / Building"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.address_line1
                            ? formData.address_line1
                            : ""
                      }
                      maxLength={150}
                      minLength={5}
                      required
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>Address Line 2</Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-opposite txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      type="text"
                      placeholder="Apartment / Unit / Subdivision"
                      disabled={isFetching ? true : false}
                      value={
                        isFetching
                          ? "Loading data..."
                          : formData.address_line2
                            ? formData.address_line2
                            : ""
                      }
                      maxLength={150}
                      minLength={5}
                      required
                      onChange={handleInputChange}
                    />
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Country <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-google-map txt-primary"></i>
                    </InputGroupText>

                    <Input
                      className="select-scroll"
                      value={formData.country}
                      id="country"
                      name="country"
                      type="select"
                      onChange={(e) => {
                        handleInputChange(e);
                        handleProvinceSelect(e);
                      }}
                      disabled={isCountryLoading}
                      required
                    >
                      <option value="" disabled>
                        {isCountryLoading
                          ? "Loading countries..."
                          : "Select Country"}
                      </option>

                      {countries.map((item) => (
                        <option key={item.code} value={item.code}>
                          {item.name}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Province <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-google-map txt-primary"></i>
                    </InputGroupText>
                    <Input
                      value={formData.province}
                      id="province"
                      name="province"
                      type="select"
                      onChange={(e) => {
                        handleInputChange(e);
                        handleCitySelect(e);
                      }}
                      required
                      disabled={
                        isProvinceLoading ||
                        !countryCode ||
                        isProvinceError ||
                        isFetching
                      }
                    >
                      <option value="" selected>
                        {isProvinceLoading
                          ? "Loading..."
                          : isProvinceError
                            ? "No Province found"
                            : "Select Province"}
                      </option>

                      {!isProvinceLoading &&
                        provinceOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    City/Municipality <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-google-map txt-primary"></i>
                    </InputGroupText>
                    <Input
                      value={formData.city}
                      id="city"
                      name="city"
                      type="select"
                      onChange={(e) => {
                        handleInputChange(e);
                        handleBarangaySelect(e);
                      }}
                      required
                      disabled={isCityLoading || !provinceId || isCityError}
                    >
                      <option value="" disabled selected>
                        {isCityLoading
                          ? "Loading..."
                          : isCityError
                            ? "No City Found"
                            : "Select City"}
                      </option>

                      {!isCityLoading &&
                        cityOptions.map((opt) => (
                          <option key={opt.id || "empty"} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>
                    Barangay <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-social-google-map txt-primary"></i>
                    </InputGroupText>
                    <Input
                      value={formData.barangay}
                      id="barangay"
                      name="barangay"
                      type="select"
                      onChange={(e) => {
                        handleInputChange(e);
                      }}
                      required
                      disabled={isBarangayLoading || !cityId || isBarangayError}
                    >
                      <option value="" disabled selected>
                        {isBarangayLoading
                          ? "Loading..."
                          : isBarangayError
                            ? "No Barangay Found"
                            : "Select Barangay"}
                      </option>

                      {!isBarangayLoading &&
                        barangayOptions.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                    </Input>
                  </InputGroup>
                </FormGroup>
              </Col>

              <Col md="4">
                <FormGroup>
                  <Label>
                    ZIP Code <b className="text-danger">*</b>
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <i className="icofont icofont-home txt-primary"></i>
                    </InputGroupText>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      type="text"
                      placeholder="XXXX"
                      maxLength={4}
                      minLength={4}
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      required
                    />
                  </InputGroup>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <div className="d-flex justify-content-end">
            <Col md="5">
              <Btn
                color="primary"
                className="w-100"
                type="submit"
                disabled={updateMutation.isPending}
              >
                <Save size={16} className="me-1" />
                {updateMutation.isPending ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updateding Applicants Address...
                  </>
                ) : (
                  "Update"
                )}
              </Btn>
            </Col>
          </div>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default UpdateAddressModal;
